import React, { useEffect, useMemo } from 'react';
import BaseModal from '../utils/BaseModal/BaseModal';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { ALBUM_MEDIA_TYPE, INFLUENCER_PERSONA_STYLE } from '../../../utils/constants';
import {
  ButtonWrapper,
  Content,
  DeleteButton,
  Form,
  MediaContainer,
  MediaContentContainer,
  PriceContainer,
  SellWithAiContainer,
  SubmitButton,
} from './EditAlbumMediaModal.styles';
import Image from '../../utils/Image/Image';
import Video from '../../utils/Video/Video';
import SmallInput from '../../utils/inputs/SmallInput/SmallInput';
import { NumberInput, StyledSelect } from '../../MediaComponent/MediaComponent.styles';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectAlbumMediaTags,
  selectShowMoreTags,
  setShowMoreTags,
} from '../../../store/slices/influencerRequest/slice';
import { selectUserActionsPending } from '../../../store/slices/user/slice';
import { deleteAlbumMedia, editAlbumMedia } from '../../../store/slices/user/asyncThunks';
import { notifyError, notifyInfo } from '../../../utils/notify';
import { useConfirmationModal } from '../ConfirmationModal/ConfirmationModal';
import Checkbox from '../../utils/Checkbox/Checkbox';

const EditAlbumMediaModal = ({ modalRef, selectedMedia, setSelectedMedia, onSuccess, influencerId }) => {
  const editMediaFormSchema = yup.object({
    description: yup.string().required('Description is required'),
    category: yup.mixed().required('Category is required'),
    cost: yup
      .number()
      .transform((value, originalValue) => {
        if (originalValue === '' || originalValue === null || Number.isNaN(value)) {
          return undefined;
        }
        return value;
      })
      .positive('Price must be positive')
      .max(500, 'Price must be at most 500')
      .notRequired(),
  });

  const dispatch = useDispatch();
  const albumMediaTags = useSelector(selectAlbumMediaTags);
  const showMore = useSelector(selectShowMoreTags);
  const { editAlbumMedia: editAlbumMediaPending, deleteAlbumMedia: deleteAlbumMediaPending } =
    useSelector(selectUserActionsPending);
  const [openModal, closeModal, setConfirmationModalLoader] = useConfirmationModal();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
    setValue,
    control,
  } = useForm({
    defaultValues: {
      description: '',
      cost: '',
      category: null,
      sellWithAi: false,
    },
    delayError: 300,
    resolver: yupResolver(editMediaFormSchema),
  });

  const onSubmit = data => {
    dispatch(editAlbumMedia({ mediaId: selectedMedia.id, ...data, ...(influencerId && { influencerId }) }))
      .unwrap()
      .then(() => {
        reset();
        if (onSuccess) {
          onSuccess();
        }
        modalRef.current.hide();
        notifyInfo('Media edited successfully!');
      })
      .catch(err => {
        notifyError(err.message);
      });
  };

  const renderContent = () => {
    switch (selectedMedia?.kind) {
      case ALBUM_MEDIA_TYPE.PHOTO:
        return <Image src={selectedMedia.url} alt="influencer-image" />;
      case ALBUM_MEDIA_TYPE.VIDEO:
        return <Video src={selectedMedia.url} />;
      default:
        break;
    }
  };

  const brandFriendlyTags = albumMediaTags
    .filter(tag => tag.type === INFLUENCER_PERSONA_STYLE.BRAND_FRIENDLY)
    .map(t => ({ label: t.value, value: t.key }));
  const sexualTags = albumMediaTags
    .filter(tag => tag.type === INFLUENCER_PERSONA_STYLE.SEXUAL)
    .map(t => ({ label: t.value, value: t.key }));

  const options = useMemo(() => {
    if (showMore) {
      return [...brandFriendlyTags, ...sexualTags];
    } else {
      return brandFriendlyTags;
    }
  }, [showMore, brandFriendlyTags, sexualTags]);

  useEffect(() => {
    if (selectedMedia) {
      setValue('description', selectedMedia.aiDescription);
      setValue(
        'category',
        [...brandFriendlyTags, ...sexualTags].find(o => o.value === selectedMedia.category),
      );
      if (selectedMedia.cost) {
        setValue('cost', selectedMedia.cost);
      }
      if (selectedMedia.caption) {
        setValue('caption', selectedMedia.caption);
      }
      if (selectedMedia.sellWithAi) {
        setValue('sellWithAi', selectedMedia.sellWithAi);
      }
    }
    // eslint-disable-next-line
  }, [selectedMedia, setValue]);

  useEffect(() => {
    setConfirmationModalLoader(deleteAlbumMediaPending);
  }, [deleteAlbumMediaPending, setConfirmationModalLoader]);

  const onSelectMenuButtonClick = () => {
    dispatch(setShowMoreTags(true));
  };

  const onDeleteMedia = () => {
    openModal(
      'Delete Media',
      `Are you sure? This action will permanently delete the media.`,
      'Delete',
      () => {
        dispatch(deleteAlbumMedia({ mediaId: selectedMedia.id, ...(influencerId && { influencerId }) }))
          .unwrap()
          .then(() => {
            closeModal();
            modalRef.current.hide();
            notifyInfo('Media deleted successfully!');
          })
          .catch(err => {
            notifyError(err.message);
          });
      },
      'Cancel',
      () => {
        closeModal();
      },
    );
  };

  const loading = editAlbumMediaPending;

  return (
    <BaseModal
      title={`Edit ${selectedMedia?.kind === ALBUM_MEDIA_TYPE.PHOTO ? 'Photo' : 'Video'}`}
      ref={modalRef}
      onClose={() => {
        modalRef.current.hide();
      }}
      onAfterClose={() => {
        reset();
        setSelectedMedia(null);
      }}>
      {selectedMedia && (
        <Form onSubmit={handleSubmit(onSubmit)}>
          <MediaContainer>
            <MediaContentContainer>{renderContent()}</MediaContentContainer>
            <Content>
              <SmallInput
                disabled={loading}
                label="Description"
                placeholder="Enter description"
                {...register('description')}
                error={errors.description?.message}
                type="textarea"
              />
              <SmallInput
                disabled={loading}
                label="Caption"
                placeholder="Enter caption"
                {...register('caption')}
                error={errors.caption?.message}
                type="textarea"
              />
              <PriceContainer>
                <Controller
                  name="category"
                  control={control}
                  render={({ field }) => {
                    return (
                      <StyledSelect
                        disabled={loading}
                        selectedOption={field.value}
                        handleChange={field.onChange}
                        label="Category"
                        error={errors.category?.message}
                        menuButtonTitle={!showMore ? 'Show Sexual' : null}
                        onMenuButtonClick={onSelectMenuButtonClick}
                        options={options}
                      />
                    );
                  }}
                />
                <NumberInput
                  disabled={loading}
                  label="Price"
                  error={errors.cost?.message}
                  prefix="$"
                  smallError
                  {...register('cost', {
                    valueAsNumber: true,
                  })}
                />
                <SellWithAiContainer>
                  <Controller
                    name="sellWithAi"
                    control={control}
                    render={({ field }) => {
                      return (
                        <Checkbox
                          disabled={loading}
                          value={field.value}
                          onChange={field.onChange}
                          label="Sell with AI"
                        />
                      );
                    }}
                  />
                </SellWithAiContainer>
              </PriceContainer>
            </Content>
          </MediaContainer>
          <ButtonWrapper>
            <DeleteButton title="Delete" onClick={onDeleteMedia} type="button" disabled={loading} />
            <SubmitButton type="submit" title="Save" isLoading={loading} />
          </ButtonWrapper>
        </Form>
      )}
    </BaseModal>
  );
};

export default EditAlbumMediaModal;
